import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import { useQuery } from '@apollo/client';
import { useFormikContext } from 'formik-2';
import classNames from 'classnames';
import WizardLayout from './WizardLayout';
import StepButtons from './StepButtons';
import css from './TestDetails.css';
import { AutoCompleteField, Spinner, TextField, WarningMessage } from '../../../../components/widgets';
import Datepicker from '../../../../components/widgets/Datepicker';
import Checkbox from '../../../../components/atoms/Checkbox';
import { DEFAULT_END_DATE, WIZARD_TEST_DETAILS } from '../consts';
import QuestionMark from '../../../../components/widgets/QuestionMark';
import { GET_APP_TO_SEGMENTS } from '../../../queries';
import useWizard from '../hook/useWizard';
import { getSegmentDropdownExtra } from './wizardUtils';
import { MIX_PANEL_KEYS, TRACK_EVENT_PREFIX } from '../../../consts';
import { trackMixpanelEvent } from '../../../../utils/general';
import { route } from '../../../../utils/routerUtils';
import GeneralPopup, { PopupTypes } from '../../../../components/organisms/GeneralPopup';
import Icon, { IconSizes } from '../../../../components/foundations/Icon';

const translatePrefix = 'STATIC.PAGES.AUDIENCES.AUDIENCE_AB_TESTING.WIZARD.SECTION_COMPONENTS.TEST_DETAILS';
export const WIZARD_DATA_TEST_ID_TEST_DETAILS = 'test-details-wizard';
const EMPTY_CUSTOM_RANGES = Object.freeze({});

const normalizeAppToSegments = (translate, appToSegments) => {
    if (!appToSegments) return [];
    return appToSegments.map(e => {
        return {
            label: `${e.appName} - ${e.platform}`,
            value: e.appsiteId,
            className: css.dropdownRow,
            icon: <img src={e.icon} alt="" className={css.icon} />,
            segments: e.segments.map(segment => ({
                label: segment.name,
                value: segment.id,
                reach: segment.reach,
                extra: getSegmentDropdownExtra(translate, translatePrefix, segment),
            })),
        };
    });
};

const TestDetails = ({ onSaveDraft, pageTitle, submitData, onNext, translate }) => {
    const { setFieldValue, values, errors, touched, dirty: isFormDirty, setFieldTouched } = useFormikContext();
    const { shouldShowDraft, isAlreadyEnded, isAlreadyStarted } = useWizard(values);
    const { loading, data: appsiteToSegmentData } = useQuery(GET_APP_TO_SEGMENTS);
    const [showPopupRedirect, setPopupRedirect] = useState(false);
    const [showAudienceWarning, setShowAudienceWarning] = useState(false);

    const normalizedAppToSegments = useMemo(
        () => normalizeAppToSegments(translate, appsiteToSegmentData?.appToSegments),
        [appsiteToSegmentData?.appToSegments, translate]
    );

    const activeSegment = appsiteToSegmentData?.appToSegments
        ?.find(appsite => appsite.appsiteId === values?.appsite?.value)
        ?.segments.find(segment => segment.id === values?.segment?.value);

    const isSegmentAlreadyShared = Boolean(activeSegment?.hasAbTest || activeSegment?.hasActiveDistribution);

    const minStartDate = useMemo(() => new Date().toISOString(), []);

    const onFieldChange = useCallback(
        (fieldName, value) => {
            setFieldTouched(fieldName, true);
            setFieldValue(fieldName, value);
        },
        [setFieldValue, setFieldTouched]
    );

    const onAppSelect = useCallback(
        selectedAppsite => {
            if (selectedAppsite?.value === values.appsite?.value) return;
            onFieldChange('appsite', selectedAppsite);
            setFieldValue('events', []);
            setFieldValue('segment', '');
        },
        [onFieldChange, setFieldValue, values.appsite?.value]
    );

    const onSegmentSelect = useCallback(
        async selectedSegment => {
            const mixPanelKey = !selectedSegment.extra
                ? MIX_PANEL_KEYS.WIZARD_SELECT_SEGMENT_THAT_IS_NOT_USED
                : MIX_PANEL_KEYS.WIZARD_SELECT_SEGMENT_THAT_IS_ALREADY_SHARED;
            trackMixpanelEvent(TRACK_EVENT_PREFIX, mixPanelKey);

            onFieldChange('segment', selectedSegment);
        },
        [onFieldChange]
    );

    const handleDatesChanged = useCallback(
        async dates => {
            if (!isAlreadyStarted) {
                if (values.isTestOngoing) {
                    // If ongoing, set both dates to the same value
                    setFieldValue('startDate', dates.start_date);
                    setFieldValue('endDate', dates.start_date);
                } else {
                    // If not ongoing, set start and end dates separately
                    setFieldValue('startDate', dates.start_date);
                    setFieldValue('endDate', dates.end_date);
                }
            } else if (!values.isTestOngoing) {
                // Already started, not ongoing - can only modify end date
                setFieldValue('endDate', dates.start_date || dates.end_date);
            }
        },
        [setFieldValue, isAlreadyStarted, values.isTestOngoing]
    );

    const onToggleOngoing = useCallback(
        async newValue => {
            setFieldValue(`isTestOngoing`, newValue);
            setFieldValue('endDate', newValue ? values.startDate : DEFAULT_END_DATE);
        },
        [setFieldValue, values.startDate]
    );

    const onAddSegmentClick = useCallback(() => {
        if (!isFormDirty || !values.appsite || isAlreadyStarted) {
            route('react/audiences/segments');
        } else {
            setPopupRedirect(true);
        }
    }, [isFormDirty, values.appsite, isAlreadyStarted]);

    const relevantAppsite = useMemo(
        () => normalizedAppToSegments?.find(app => app.value === values.appsite?.value)?.segments || [],
        [normalizedAppToSegments, values.appsite]
    );

    const onTestNameChange = useCallback(async val => onFieldChange('testName', val), [onFieldChange]);

    const handleNext = useCallback(() => {
        if (isSegmentAlreadyShared && !values.status) {
            setShowAudienceWarning(true);
        } else {
            onNext();
        }
    }, [isSegmentAlreadyShared, values.status, onNext]);

    const handleContinueAnyway = useCallback(() => {
        setShowAudienceWarning(false);
        onNext();
    }, [onNext]);

    const handleSelectDifferentAudience = useCallback(() => {
        setShowAudienceWarning(false);
    }, []);

    if (loading) {
        return <Spinner show expanded />;
    }

    return (
        <WizardLayout
            activeKey={WIZARD_TEST_DETAILS}
            title={pageTitle}
            isContentScrollable={false}
            onSaveDraft={onSaveDraft}
            submitData={submitData}
            shouldShowDraft={shouldShowDraft}
            buttons={<StepButtons onNext={handleNext} nextTitle="STATIC.BUTTONS.NEXT" />}
        >
            <div className={css.container} data-testid={WIZARD_DATA_TEST_ID_TEST_DETAILS}>
                <div className={css.subTitle}>
                    <Translate id={`${translatePrefix}.SUBTITLE`} />
                </div>
                <TextField
                    autoCompleteOff
                    containerClass={css.formItem}
                    labelTextClass={css.label}
                    onChange={val => onTestNameChange(val)}
                    label={`${translatePrefix}.TEST_NAME.DISPLAY_NAME`}
                    placeholder={`${translatePrefix}.TEST_NAME.PLACEHOLDER`}
                    disabled={isAlreadyEnded || isAlreadyStarted}
                    value={values.testName}
                    error={touched.testName && errors.testName}
                    debounce={400}
                />
                <TextField
                    labelTextClass={css.label}
                    onChange={val => setFieldValue(`description`, val)}
                    containerClass={css.formItem}
                    inputConfig={{ rows: 3 }}
                    disabled={isAlreadyEnded}
                    value={values.description}
                    label={`${translatePrefix}.TEST_DESCRIPTION.DISPLAY_NAME`}
                    placeholder={`${translatePrefix}.TEST_DESCRIPTION.PLACEHOLDER`}
                    debounce={400}
                />
                <div className={css.formItem}>
                    <div className={css.label}>
                        <Translate id={`${translatePrefix}.APPSITE.DISPLAY_NAME`} />
                    </div>
                    <AutoCompleteField
                        isMulti={false}
                        placeholder={`${translatePrefix}.APPSITE.PLACEHOLDER`}
                        wrapperClass={css.wrapper}
                        options={normalizedAppToSegments}
                        disabled={values.status}
                        value={values.appsite}
                        error={touched.appsite && errors.appsite}
                        onChange={onAppSelect}
                    />
                </div>
                <div className={css.formItem}>
                    <div className={classNames(css.label, css.iconWithText)}>
                        {isSegmentAlreadyShared && (
                            <Icon name="info-triangle" size={IconSizes.SMALL} color="var(--warning1Color)" />
                        )}
                        <span>
                            {translate(`${translatePrefix}.SEGMENT.DISPLAY_NAME`)}
                            <span className={css.spacer} />
                            <Translate id={`${translatePrefix}.SEGMENT.CREATE_NEW`} />
                            <span className={css.link} onClick={onAddSegmentClick}>
                                <Translate id={`${translatePrefix}.SEGMENT.SEGMENTS`} />
                            </span>
                        </span>
                    </div>
                    <AutoCompleteField
                        isMulti={false}
                        value={values.segment}
                        error={touched.segment && errors.segment}
                        wrapperClass={css.wrapper}
                        options={relevantAppsite}
                        controlled
                        disabled={values.status || !values.appsite}
                        placeholder={`${translatePrefix}.SEGMENT.PLACEHOLDER`}
                        onChange={onSegmentSelect}
                    />
                    <WarningMessage
                        className={css.segmentWarningContainer}
                        message={`${translatePrefix}.SEGMENT.WARNING_RESULT`}
                        show={isSegmentAlreadyShared}
                        showIcon={false}
                        iconAlign="top"
                        showSpacer={false}
                    />
                </div>
                <div className={css.dateContainer}>
                    <div className={css.dateTitleContainer}>
                        <span className={css.checkmarkLabel}>
                            <Translate id={`${translatePrefix}.EXPERIMENT_PERIOD.DISPLAY_NAME`} />
                        </span>
                        <QuestionMark message={`${translatePrefix}.EXPERIMENT_PERIOD.TOOLTIP`} />
                    </div>
                    <Datepicker
                        minDate={minStartDate}
                        onApply={handleDatesChanged}
                        drops="up"
                        ranges={EMPTY_CUSTOM_RANGES}
                        autoApply
                        singleDatePicker={values.isTestOngoing || (isAlreadyStarted && !values.isTestOngoing)}
                        disabled={isAlreadyEnded || (isAlreadyStarted && values.isTestOngoing)}
                        buttonType="button"
                        startDate={!isAlreadyStarted || values.isTestOngoing ? values.startDate : values.endDate}
                        endDate={values.endDate}
                    />
                    <div className={css.checkboxContainer}>
                        <Checkbox
                            checked={values.isTestOngoing}
                            size="small"
                            disabled={isAlreadyEnded}
                            onChange={onToggleOngoing}
                            labelClass={css.checkmarkLabel}
                            label={<Translate id={`${translatePrefix}.IS_TEST_ONGOING.DISPLAY_NAME`} />}
                            labelTooltip={`${translatePrefix}.IS_TEST_ONGOING.TOOLTIP`}
                        />
                    </div>
                </div>
                <GeneralPopup
                    open={showPopupRedirect}
                    type={PopupTypes.DELETE}
                    rejectText="STATIC.PAGES.AUDIENCES.AUDIENCE_AB_TESTING.WIZARD.CONTINUE_EDIT"
                    acceptText="STATIC.BUTTONS.SAVE"
                    title="STATIC.PAGES.AUDIENCES.AUDIENCE_AB_TESTING.WIZARD.SAVE_DRAFT_TITLE"
                    text={`${translatePrefix}.SEGMENT.REDIRECT_TITLE`}
                    onAccept={() => route('react/audiences/segments')}
                    onReject={() => setPopupRedirect(false)}
                />
                <GeneralPopup
                    open={showAudienceWarning}
                    type={PopupTypes.INFO_WITH_CANCEL}
                    rejectText={`${translatePrefix}.SEGMENT.SELECT_DIFFERENT_AUDIENCE`}
                    acceptText={`${translatePrefix}.SEGMENT.CONTINUE_ANYWAY`}
                    title={
                        <div className={classNames(css.iconWithText)}>
                            <Icon name="info-triangle" size={IconSizes.SMALL} color="var(--warning1Color)" />
                            <span>{translate(`${translatePrefix}.SEGMENT.AUDIENCE_WARNING_TITLE`)}</span>
                        </div>
                    }
                    text={`${translatePrefix}.SEGMENT.AUDIENCE_WARNING_TEXT`}
                    onAccept={handleContinueAnyway}
                    onReject={handleSelectDifferentAudience}
                />
            </div>
        </WizardLayout>
    );
};

TestDetails.propTypes = {
    onSaveDraft: PropTypes.func,
    translate: PropTypes.func,
    onNext: PropTypes.func,
    pageTitle: PropTypes.string,
    submitData: PropTypes.shape({
        loading: PropTypes.bool,
        error: PropTypes.bool,
    }),
};

export default withLocalize(TestDetails);
